import { useState, useEffect, useCallback } from 'react'
import { LayoutGrid, LayoutList, Tag, Download, Trash2, X, Upload } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import ArchiveList from './ArchiveList'
import ArchiveTable from './ArchiveTable'
import ArchiveDetail from './ArchiveDetail'
import ArchiveUpload from './ArchiveUpload'
import BulkTagModal from '../../../components/BulkTagModal'
import '../../../components/BulkTagModal.css'
import './ArchiveManager.css'

export default function ArchiveManager() {
  const { api, apiKey } = useAuth()
  const [files, setFiles] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [stats, setStats] = useState({ count: 0, totalSize: 0 })
  const [viewMode, setViewMode] = useState('table')

  // Tag filter
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterKeys, setTagFilterKeys] = useState(null)

  // Bulk selection
  const [checked, setChecked] = useState(new Set())
  const [showBulkTag, setShowBulkTag] = useState(false)

  useEffect(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (!activeTag) {
      setTagFilterKeys(null)
      return
    }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}`)
      .then(d => setTagFilterKeys(d.archive_keys || []))
      .catch(() => setTagFilterKeys([]))
  }, [activeTag, api])

  const loadFiles = useCallback(async () => {
    setLoading(true)
    setStatus('Loading archive...')

    try {
      const data = await api('/archive')
      const list = data.files || []

      const totalSize = list.reduce((sum, f) => sum + (f.size || 0), 0)
      setStats({ count: list.length, totalSize })

      setFiles(list)
      setStatus(`${list.length} files in archive`)
    } catch (e) {
      console.error('Load archive error:', e)
      setStatus(`Error: ${e.message}`)
      setFiles([])
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  function handleSelect(file) {
    setSelected(file)
  }

  async function handleDelete(filename) {
    try {
      setStatus('Deleting...')
      await api(`/archive/${encodeURIComponent(filename)}`, { method: 'DELETE' })
      setSelected(null)
      setStatus('✅ File deleted from archive')
      loadFiles()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleRename(oldName, newName) {
    try {
      setStatus('Renaming...')
      await api('/archive/rename', {
        method: 'POST',
        body: JSON.stringify({ oldName, newName })
      })
      setStatus('✅ File renamed')
      loadFiles()
      if (selected?.filename === oldName) {
        setSelected(prev => ({ ...prev, filename: newName }))
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleUploadComplete() {
    setStatus('✅ Upload complete')
    loadFiles()
  }

  // Bulk selection helpers
  function handleToggle(filename) {
    setChecked(prev => {
      const next = new Set(prev)
      next.has(filename) ? next.delete(filename) : next.add(filename)
      return next
    })
  }

  function handleToggleAll(displayFiles) {
    if (checked.size === displayFiles.length && displayFiles.every(f => checked.has(f.filename))) {
      setChecked(new Set())
    } else {
      setChecked(new Set(displayFiles.map(f => f.filename)))
    }
  }

  function clearSelection() {
    setChecked(new Set())
  }

  function getCheckedItems() {
    return files
      .filter(f => checked.has(f.filename))
      .map(f => ({ id: f.filename, title: f.filename }))
  }

  // Bulk download
  function handleBulkDownload() {
    const API_BASE = import.meta.env.VITE_API_BASE || '/api'
    const filenames = [...checked]
    filenames.forEach((filename, i) => {
      setTimeout(() => {
        const url = `${API_BASE}/archive/download/${encodeURIComponent(filename)}?key=${encodeURIComponent(apiKey)}`
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }, i * 500)
    })
    setStatus(`⬇️ Downloading ${filenames.length} files`)
  }

  // Bulk delete
  async function handleBulkDelete() {
    const filenames = [...checked]
    if (!confirm(`Delete ${filenames.length} files from archive?\n\nThis cannot be undone!`)) return

    setStatus(`Deleting ${filenames.length} files...`)
    let success = 0
    let errors = 0

    for (const filename of filenames) {
      try {
        await api(`/archive/${encodeURIComponent(filename)}`, { method: 'DELETE' })
        success++
        setStatus(`Deleting ${success}/${filenames.length}...`)
      } catch {
        errors++
      }
    }

    setStatus(`✅ ${success} deleted${errors ? `, ${errors} failed` : ''}`)
    clearSelection()
    setSelected(null)
    loadFiles()
  }

  // Bulk restore to Cloudflare Stream
  async function handleBulkRestore() {
    const filenames = [...checked]
    if (!confirm(`Restore ${filenames.length} files to Cloudflare Stream?\n\nEach file will be uploaded with its archive title (prefixed with "RESTORE -").`)) return

    setStatus(`⏳ Restoring ${filenames.length} files to Stream...`)
    let success = 0
    let errors = 0

    for (const filename of filenames) {
      const file = files.find(f => f.filename === filename)
      const meta = file?.customMetadata || {}
      const originalTitle = meta.originalTitle || filename.replace(/\.mp4$/i, '')
      const title = `RESTORE - ${originalTitle}`

      try {
        await api('/restore-to-stream', {
          method: 'POST',
          body: JSON.stringify({ filename, title }),
        })
        success++
        setStatus(`⏳ Restoring ${success}/${filenames.length}...`)
      } catch {
        errors++
      }
    }

    setStatus(`✅ ${success} restored to Stream${errors ? `, ${errors} failed` : ''}`)
    clearSelection()
  }

  // Filter files by search and tag
  const filtered = files.filter(f => {
    if (searchTerm && !f.filename.toLowerCase().includes(searchTerm.toLowerCase())) return false
    if (tagFilterKeys !== null && !tagFilterKeys.includes(f.filename)) return false
    return true
  })

  return (
    <div className="archive-manager">
      <div className="archive-manager-header">
        <h1>Archive Manager</h1>
        <div className="am-header-right">
          <div className="sm-view-toggle">
            <button
              className={`sm-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`sm-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table view"
            >
              <LayoutList size={16} />
            </button>
          </div>
          <div className="archive-stats">
            <span className="stat">{stats.count} files</span>
            <span className="stat-divider">·</span>
            <span className="stat">{formatBytes(stats.totalSize)}</span>
            <span className="stat-divider">·</span>
            <span className="status-text">{status}</span>
          </div>
        </div>
      </div>

      <div className={`archive-manager-layout ${viewMode === 'table' ? 'am-layout-table' : 'am-layout-cards'}`}>
        {viewMode === 'cards' ? (
          <>
            <div className="archive-manager-sidebar">
              <ArchiveList
                files={filtered}
                selected={selected}
                loading={loading}
                searchTerm={searchTerm}
                onSearch={setSearchTerm}
                onSelect={handleSelect}
                onRefresh={loadFiles}
                allTags={allTags}
                activeTag={activeTag}
                onTagFilter={setActiveTag}
                checked={checked}
                onToggle={handleToggle}
                onToggleAll={handleToggleAll}
                onClearSelection={clearSelection}
                onBulkTag={() => setShowBulkTag(true)}
                onBulkDownload={handleBulkDownload}
                onBulkRestore={handleBulkRestore}
                onBulkDelete={handleBulkDelete}
              />
            </div>

            <div className="archive-manager-main">
              {selected ? (
                <ArchiveDetail
                  file={selected}
                  onDelete={handleDelete}
                  onRename={handleRename}
                  setStatus={setStatus}
                />
              ) : (
                <div className="no-selection">
                  <p>Select a file from the archive to view details</p>
                </div>
              )}

              <ArchiveUpload onComplete={handleUploadComplete} setStatus={setStatus} />
            </div>
          </>
        ) : (
          <>
            {/* Table mode: split detail top, filters + table bottom */}
            <div className="am-detail-panel">
              {selected ? (
                <ArchiveDetail
                  file={selected}
                  onDelete={handleDelete}
                  onRename={handleRename}
                  setStatus={setStatus}
                  layout="split"
                  uploadSlot={<ArchiveUpload onComplete={handleUploadComplete} setStatus={setStatus} />}
                />
              ) : (
                <div className="no-selection">
                  <p>Select a file from the table below to view details</p>
                  <ArchiveUpload onComplete={handleUploadComplete} setStatus={setStatus} />
                </div>
              )}
            </div>

            {/* Filters bar */}
            <div className="am-filters-bar">
              <div className="am-filter-search">
                <input
                  type="text"
                  placeholder="Filter archive..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="am-filter-tag">
                <select value={activeTag} onChange={e => setActiveTag(e.target.value)}>
                  <option value="">All Tags</option>
                  {(allTags || []).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <button onClick={loadFiles} className="secondary" style={{ fontSize: 12, padding: '3px 10px' }}>↻ Refresh</button>

              {checked.size > 0 && (
                <div className="am-filter-bulk">
                  <span>{checked.size} sel</span>
                  <button onClick={() => setShowBulkTag(true)}><Tag size={12} /> Tag</button>
                  <button onClick={handleBulkDownload}><Download size={12} /> DL</button>
                  <button onClick={handleBulkRestore}><Upload size={12} /> Restore</button>
                  <button onClick={handleBulkDelete} className="danger"><Trash2 size={12} /> Del</button>
                  <button onClick={clearSelection}><X size={12} /></button>
                </div>
              )}
            </div>

            <ArchiveTable
              files={filtered}
              loading={loading}
              selected={selected}
              onSelect={handleSelect}
              checked={checked}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
            />
          </>
        )}
      </div>

      {showBulkTag && (
        <BulkTagModal
          items={getCheckedItems()}
          idKey="archive_key"
          onClose={() => setShowBulkTag(false)}
          onComplete={() => {
            setShowBulkTag(false)
            setStatus('✅ Bulk tags applied')
            clearSelection()
            api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
          }}
        />
      )}
    </div>
  )
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}
